import React, { Component } from 'react';

import get from 'lodash/get';
import keys from 'lodash/keys';
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import Country from 'root/apps/Address/Country';
import Region from 'root/apps/Address/Region';
import {
  getCountryFromCountryName,
  getL10nForCountryCode,
} from 'root/apps/Autocomplete/Country/utils';
import { getRegionFromRegionName, hasRegions } from 'root/apps/Autocomplete/Region/utils';
import { createReactRenderer } from 'root/apps/helpers/react-renderer';

const FormRow = ({ children, label, name }) => (
  <tr>
    {label ? (
      <td className="table-address__label label">
        <label htmlFor={name}>{label}</label>
      </td>
    ) : null}
    <td className="table-address__value">{children}</td>
  </tr>
);

const getLocationValue = (rows, key) =>
  get(
    rows.find((o) => o[key]),
    key,
    ''
  );

class Address extends Component {
  constructor(props) {
    super(props);

    const { rows } = this.props;

    let countryCode = getLocationValue(rows, 'countryCode');
    let countryName = getLocationValue(rows, 'countryName');
    let regionCode = getLocationValue(rows, 'regionCode');
    let regionName = getLocationValue(rows, 'regionName');

    if (countryName && !countryCode) {
      ({ countryCode, countryName } = getCountryFromCountryName(countryName));
    }

    if (countryCode && hasRegions(countryCode)) {
      ({ regionCode, regionName } = getRegionFromRegionName({
        countryCode,
        regionName: regionCode || regionName,
      }));
    }

    this.state = {
      // eslint-disable-next-line react/no-unused-state
      countryCode,
      // eslint-disable-next-line react/no-unused-state
      countryName,
      regionCode,
      regionName,
      tableRef: React.createRef(),
      l10nTranslations: getL10nForCountryCode(countryCode).translations,
      autoCompleteErrorMap: {},
    };
  }

  componentDidMount() {
    (this.props.rows ?? []).forEach((row) => {
      if (row.component === 'Country') {
        this.validateAutoCompleteWithId(row.id, { value: row.countryName });
      } else if (row.component === 'Region') {
        this.validateAutoCompleteWithId(row.id, { value: row.regionName });
      }
    });
  }

  handleCountrySelectChange(option) {
    this.handleChanged();

    let { regionCode, regionName } = this.state;
    const { countryCode = '', countryName = '' } = option || {};

    // Reset regionCode on country change
    regionCode = '';

    if (countryCode && hasRegions(countryCode)) {
      ({ regionCode, regionName } = getRegionFromRegionName({
        countryCode,
        regionName: regionCode || regionName,
      }));
    }

    this.setState({
      // eslint-disable-next-line react/no-unused-state
      countryCode,
      // eslint-disable-next-line react/no-unused-state
      countryName,
      regionCode,
      regionName,
      l10nTranslations: getL10nForCountryCode(countryCode).translations,
    });

    (this.props.rows ?? []).forEach((row) => {
      if (row.component === 'Region') {
        this.validateAutoCompleteWithId(row.id, { value: regionName });
      }
    });
  }

  handleCountryInputChange(countryName) {
    this.handleChanged();

    this.setState({
      // eslint-disable-next-line react/no-unused-state
      countryName,
    });
  }

  handleRegionSelectChange(option) {
    this.handleChanged();

    const { value: regionCode = '', label: regionName = '' } = option || {};

    this.setState({
      regionName,
      regionCode,
    });
  }

  handleRegionInputChange(regionName) {
    this.handleChanged();

    this.setState({
      regionName,
    });
  }

  handleChanged() {
    if (!this.props.do_saves) return;
    if (!this.isChanged) {
      // Whenever a value is changed from a gui_def form, the save button changes from
      // light gray to blue suggesting to save the current page. Gui_def also saves
      // a `do_save` variable in the window scope so if the user wants to browse
      // away it prompts to save or omit the current changes. Since the Address
      // component is inside gui_def layout the same functions and behaviours are applied.
      window.viewFieldChanged('view');
      this.isChanged = true;
    }
  }

  handleKeyPress(event) {
    if (!this.props.do_saves) return;
    if (
      // On "Enter" key, the gui_def function to save the current form is called.
      window.onEnterKey(event, () => {
        window.saveRecord('view');
      })
    ) {
      // eslint-disable-next-line consistent-return
      return false;
    }
  }

  validateFieldFromEvent(event) {
    if (event.target) {
      window.validateField(event.target);
    }
  }

  validateAutoCompleteWithId(id, options = {}) {
    const input = this.state.tableRef.current?.querySelector(`#${id}`);
    if (input) {
      const hasError = !window.validateField(input, options);
      this.setState((prevState) => {
        if (prevState.autoCompleteErrorMap[id] !== hasError) {
          return {
            autoCompleteErrorMap: {
              ...prevState.autoCompleteErrorMap,
              [id]: hasError,
            },
          };
        }
        return null;
      });
    }
  }

  render() {
    const { rows, tabIndex } = this.props;
    const { l10nTranslations, ...localization } = this.state;

    return (
      <table className="table-address" ref={this.state.tableRef}>
        <tbody>
          {rows.map((row) => {
            const {
              value,
              name,
              id = name,
              label,
              placeholder,
              className = 'view_view',
              component,
              inputProps,
              ...otherProps
            } = omit(row, keys(localization));

            // eslint-disable-next-line max-len
            const l10nTranslationsPlaceholder =
              l10nTranslations[(placeholder || label).toLowerCase()] || placeholder || label;

            const baseProps = {
              id,
              name,
              className,
              tabIndex,
              placeholder:
                typeof placeholder !== 'undefined' ? placeholder : l10nTranslationsPlaceholder,
            };
            const localizationProps = pick(localization, keys(row));

            const hasAutoCompleteError = this.state.autoCompleteErrorMap[id];

            return (
              <FormRow label={label && l10nTranslationsPlaceholder} name={name} key={name}>
                {component ? (
                  <div className="cirrus">
                    {component === 'Country' ? (
                      <Country
                        autocompleteClassName={`gui-def-autocomplete ${
                          hasAutoCompleteError ? 'cr-autocomplete--error' : ''
                        }`}
                        {...baseProps}
                        onChange={(option) => {
                          this.handleCountrySelectChange(option);
                          this.validateAutoCompleteWithId(
                            id,
                            option?.countryName ? { value: option.countryName } : undefined
                          );
                        }}
                        onInputChange={(inputValue) => {
                          this.handleCountryInputChange(inputValue);
                          this.validateAutoCompleteWithId(id, { value: inputValue });
                        }}
                        inputProps={inputProps}
                        {...localizationProps}
                        {...otherProps}
                      />
                    ) : null}
                    {component === 'Region' ? (
                      <Region
                        autocompleteClassName={`gui-def-autocomplete ${
                          hasAutoCompleteError ? 'cr-autocomplete--error' : ''
                        }`}
                        {...baseProps}
                        onChange={(option) => {
                          this.handleRegionSelectChange(option);
                          this.validateAutoCompleteWithId(
                            id,
                            option?.regionName ? { value: option.regionName } : undefined
                          );
                        }}
                        onInputChange={(inputValue) => {
                          this.handleRegionInputChange(inputValue);
                          this.validateAutoCompleteWithId(id, { value: inputValue });
                        }}
                        onKeyPress={(option) => this.handleKeyPress(option)}
                        inputProps={inputProps}
                        {...localizationProps}
                        {...otherProps}
                      />
                    ) : null}
                  </div>
                ) : (
                  <input
                    {...baseProps}
                    type="text"
                    autoComplete="off"
                    defaultValue={value}
                    size="30"
                    maxLength="255"
                    onChange={(event) => {
                      this.handleChanged(event);
                      this.validateFieldFromEvent(event);
                    }}
                    data-validation="checkForCreditCardInfo"
                    data-container-selector=".view_field_box,.register-edit-ship-to"
                    onKeyPress={(event) => this.handleKeyPress(event)}
                    onBlur={(event) => this.validateFieldFromEvent(event)}
                    style={{ height: '32px', lineHeight: '32px' }}
                    {...otherProps}
                  />
                )}
              </FormRow>
            );
          })}
        </tbody>
      </table>
    );
  }
}

const RenderedComponent = createReactRenderer({
  component: Address,
  selector: '[data-react-root="Address"]',
  callback: () => {
    // Toggle container visibility on register page to fix an async loading issue explained below
    if (window.location.pathname === '/register.php') {
      const shipToWrapper = document.getElementById('ship_to_data');
      if (shipToWrapper instanceof HTMLElement) {
        document.getElementById('ship_to_data').style.display = 'block';
        document.getElementById('ship_to_data_loading').style.display = 'none';
        window.validateFieldsInContainer(shipToWrapper);
      }
    }
  },
});

history.onChangeState(
  [
    {
      name: [
        'customer.views.customer',
        'customer.views.register_customer',
        'admin.views.shop',
        'admin.views.employee',
        'credit_account.views.credit_account',
        'vendor.views.vendor',
        'ship_to.views.ship_to',
      ],
      id: undefined,
    },
    { pathname: '/register.php', form_name: undefined },
    { name: 'workbench.views.beta_workorder', tab: 'details' },
    { name: 'transaction.views.transaction', tab: 'ship_to', id: undefined },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default Address;
